import { FC, useMemo } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { Formik } from 'formik';
import { type FormikHelpers } from 'formik';
import { EMAIL_FORM, EMAIL_SCHEMA } from 'modules/account/contact/forms/schema';
import { useGetLead } from 'modules/account/contact/hooks';
import { useOutlookSendMail } from 'modules/account/inbox/hooks';
import constants from 'modules/account/inbox/utils/constants';
import { useParams } from 'react-router-dom';
import ComposerEmailForm from 'ui/partials/Composer/ComposerEmailForm';
import { OutlookMessage, Token } from 'utils/types';
import { OUTLOOK_SENDMAIL_FORM } from 'modules/account/inbox/forms/schema';

type OutlookComposerEmailProps = {
  token: Token;
  attachments: File[];
  setAttachments: React.Dispatch<React.SetStateAction<File[]>>;
  closeComposer: () => void;
  setMessages?: React.Dispatch<React.SetStateAction<OutlookMessage[]>>;
};

const OutlookComposerEmail: FC<OutlookComposerEmailProps> = ({
  token,
  attachments,
  closeComposer,
  setAttachments,
  setMessages,
}) => {
  const queryClient = useQueryClient();
  const params = useParams();
  const { data } = useGetLead(params?.id as string);

  const { mutate: sendMail } = useOutlookSendMail();

  const defaultValue = useMemo(() => {
    if (!data) return null;
    return {
      label: data?.fullName ?? `${data?.firstName} ${data?.lastName}`,
      value: data?.emailAddress,
    };
  }, [data]);

  const from = token.meta?.emailAddress;

  const initialValues: EMAIL_FORM = {
    to: defaultValue ? [defaultValue] : [],
    from,
    cc: [],
    bcc: [],
    subject: '',
    text: '',
  };
  const filetoBase64 = (file: Blob) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });

  const onSubmit = async (
    value: EMAIL_FORM,
    { setSubmitting, resetForm }: FormikHelpers<EMAIL_FORM>,
  ) => {
    const castValues = EMAIL_SCHEMA.cast(value);

    const payload: OUTLOOK_SENDMAIL_FORM = {
      subject: castValues.subject,
      from: castValues.from as string,
      toRecipients: castValues.to.map((val) => ({
        emailAddress: { address: val.value },
      })),
      ccRecipients:
        castValues.cc?.map((val) => ({
          emailAddress: { address: val.value },
        })) ?? [],
      bccRecipients:
        castValues.bcc?.map((val) => ({
          emailAddress: { address: val.value },
        })) ?? [],
      body: {
        content: castValues.text?.replace(
          /<p(\b[^>]*)>/g,
          '<p$1 style="margin:0;">',
        ),
        contentType: 'HTML',
      },
      attachments: await Promise.all(
        attachments?.map(async (attachment: File) => {
          const base64 = (await filetoBase64(attachment)) as string;
          const ms_base64 = base64.split(',')[1];
          return {
            name: attachment.name,
            contentType: attachment.type,
            contentBytes: ms_base64,
            '@odata.type': '#microsoft.graph.fileAttachment',
          };
        }),
      ),
    };

    await sendMail(payload, {
      onSuccess: () => {
        setSubmitting(false);
        closeComposer();
        setMessages?.([]);
        resetForm();
        queryClient.invalidateQueries({
          queryKey: [constants.OUTLOOK_MESSAGES],
        });
      },
    });
  };

  return (
    <Formik
      enableReinitialize
      onSubmit={onSubmit}
      initialValues={initialValues}
      validationSchema={EMAIL_SCHEMA}
    >
      {() => (
        <ComposerEmailForm
          attachments={attachments}
          setAttachments={setAttachments}
        />
      )}
    </Formik>
  );
};

export default OutlookComposerEmail;
